// Rewards & Leveling ladder (jbolter/aifans-project#220). Progression is lifetime tokens SPENT.
// Shared by the server (bonus crediting) and the web client (level display).

export type RewardLevelDef = { level: number; name: string; threshold: number; bonus: number }

export const REWARD_LEVELS: RewardLevelDef[] = [
  { level: 1, name: 'Newcomer', threshold: 0, bonus: 0 },
  { level: 2, name: 'Bronze', threshold: 100, bonus: 2 },
  { level: 3, name: 'Silver', threshold: 300, bonus: 5 },
  { level: 4, name: 'Gold', threshold: 750, bonus: 12 },
  { level: 5, name: 'Platinum', threshold: 1500, bonus: 30 },
  { level: 6, name: 'Diamond', threshold: 3000, bonus: 60 },
  { level: 7, name: 'Master', threshold: 6000, bonus: 150 },
  { level: 8, name: 'Grandmaster', threshold: 10000, bonus: 240 },
  { level: 9, name: 'Legend', threshold: 16000, bonus: 360 },
  { level: 10, name: 'Mythic', threshold: 25000, bonus: 630 },
]

export const PRESTIGE_STEP = 10_000
export const PRESTIGE_BONUS = 700

const TOP = REWARD_LEVELS[REWARD_LEVELS.length - 1]

export type RewardLevelInfo = {
  lifetime: number
  level: number
  name: string
  threshold: number
  prestige: number
  next: RewardLevelDef
  /** 0..1 progress from the current level's threshold to the next one */
  progress: number
  tokensToNext: number
}

export type RewardLevelUp = { level: number; name: string; bonus: number }

function clean(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
}

export function levelDef(level: number): RewardLevelDef {
  if (level <= TOP.level) return REWARD_LEVELS[Math.max(1, level) - 1]
  const n = level - TOP.level
  return {
    level,
    name: `Prestige ${n}`,
    threshold: TOP.threshold + n * PRESTIGE_STEP,
    bonus: PRESTIGE_BONUS,
  }
}

function levelForLifetime(lifetime: number): number {
  if (lifetime >= TOP.threshold) {
    return TOP.level + Math.floor((lifetime - TOP.threshold) / PRESTIGE_STEP)
  }
  let level = 1
  for (const def of REWARD_LEVELS) {
    if (lifetime >= def.threshold) level = def.level
  }
  return level
}

export function getRewardLevel(lifetimeRaw: number | undefined | null): RewardLevelInfo {
  const lifetime = clean(lifetimeRaw)
  const level = levelForLifetime(lifetime)
  const current = levelDef(level)
  const next = levelDef(level + 1)
  const span = next.threshold - current.threshold
  return {
    lifetime,
    level,
    name: current.name,
    threshold: current.threshold,
    prestige: Math.max(0, level - TOP.level),
    next,
    progress: span > 0 ? (lifetime - current.threshold) / span : 0,
    tokensToNext: next.threshold - lifetime,
  }
}

export function getBonusesCrossed(prevLifetime: number, newLifetime: number): RewardLevelUp[] {
  const from = levelForLifetime(clean(prevLifetime))
  const to = levelForLifetime(clean(newLifetime))
  const ups: RewardLevelUp[] = []
  for (let level = from + 1; level <= to; level++) {
    const def = levelDef(level)
    ups.push({ level: def.level, name: def.name, bonus: def.bonus })
  }
  return ups
}
